<template>
  <VLayout row wrap>
    <VFlex xs12>
      <VCard>
        <VCardTitle>
          <h2>Računi</h2>
        </VCardTitle>
        <VLayout row class="justify-space-between">
          <VLayout row>
            <VFlex xs3 class="ml-4">
              <VMenu
                ref="fromDate"
                v-model="fromMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <VTextField
                  slot="activator"
                  :value="computedFromDate"
                  label="Obdobje od"
                  prepend-icon="event"
                  clearable
                  readonly
                />
                <VDatePicker v-model="fromDate" no-title :max="toDate" @change="fromMenu = false" />
              </VMenu>
            </VFlex>
            <VFlex xs3 class="ml-4">
              <VMenu
                ref="toDate"
                v-model="toMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <VTextField
                  slot="activator"
                  :value="computedToDate"
                  label="Obdobje do"
                  prepend-icon="event"
                  clearable
                  readonly
                />
                <VDatePicker v-model="toDate" :min="fromDate" no-title @change="toMenu = false" />
              </VMenu>
            </VFlex>
            <VFlex xs4 class="ml-4">
              <VAutocomplete
                v-model="selectedPartner"
                label="Partner"
                :items="partnerSearchResults"
                :search-input.sync="partnerSearchQuery"
                item-text="displayText"
                item-value="id"
                :loading="loadingPartners"
                clearable
                no-data-text="Ni zadetkov"
                placeholder="Iščite po imenu ali davčni"
                return-object
              />
            </VFlex>
          </VLayout>
          <VFlex xs5 style="display: flex; justify-content: end">
            <VFlex xs7>
              <VTextField
                v-model="searchModel"
                append-icon="search"
                label="Iskalni niz"
                single-line
                hide-details
              />
            </VFlex>
            <VBtn
              class="ma-3 px-5"
              depressed
              small
              color="primary"
              @click="queryInvoicesWithParams"
            >
              Išči
            </VBtn>
          </VFlex>
        </VLayout>
        <VLayout row class="ml-5 mr-3">
          <VFlex xs2>
            <VSelect v-model="documentSource" label="Vir" :items="documentSourceItems" />
          </VFlex>
          <VFlex xs2 pl-3>
            <VSelect v-model="documentType" label="Vrsta" :items="documentTypeItems" />
          </VFlex>
          <VFlex xs2 pl-3>
            <VSelect v-model="documentStatus" label="Status" :items="documentStatusItems" />
          </VFlex>
          <VFlex xs2 pl-3>
            <VSelect
              v-model="selectedExportPath"
              label="Izvozna pot"
              :items="exportPathItems"
              clearable
            />
          </VFlex>
          <VFlex xs4 pl-3>
            <VLayout row align-center>
              <VFlex grow>
                <VTextField
                  v-model="clientFilter"
                  append-icon="filter_list"
                  label="Filter tabele (ID, Partner, Pošiljatelj, Priponka, Zadeva, Datum izvoza)"
                  hide-details
                  clearable
                  placeholder="Vnesite besedilo za filtriranje trenutnih rezultatov..."
                />
              </VFlex>
              <VTooltip left>
                <VBtn
                  slot="activator"
                  icon
                  color="secondary"
                  class="ma-0 ml-3"
                  @click="exportCsv"
                  :loading="exportingCsv"
                >
                  <VIcon>cloud_download</VIcon>
                </VBtn>
                <span>Izvoz rezultatov v CSV</span>
              </VTooltip>
            </VLayout>
          </VFlex>
        </VLayout>
        <v-card-text style="max-height: 55vh; overflow: auto">
          <VDataTable
            :headers="headers"
            :items="filteredInvoices"
            :pagination.sync="pagination"
            :total-items="clientFilter ? filteredInvoices.length : invoiceCount"
            :loading="loading"
            rows-per-page-text="Vrstic na stran"
            :rows-per-page-items="[5, 10, 25, 50, 100]"
          >
            <template slot="items" slot-scope="props">
              <td class="text-xs-center">
                {{ props.item.url.split('/').slice(-2, -1)[0] }}
              </td>
              <td class="text-xs-center">
                {{ props.item.sent_date | formatDateFilter }}
              </td>
              <td class="text-xs-center">
                {{ props.item.from_mail }}
              </td>
              <td class="text-xs-center">
                {{ props.item.subject }}
              </td>
              <td class="text-xs-center">
                {{ props.item.file_name }}
              </td>
              <td class="text-xs-center">
                {{ props.item.partner_name }}
              </td>
              <td class="text-xs-center">
                {{ props.item.partner_vat_id }}
              </td>
              <td class="text-xs-center">
                {{ props.item.partner_country }}
              </td>
              <td class="text-xs-center">
                {{ props.item.export_path }}
              </td>
              <td class="text-xs-center">
                {{ props.item.external_id }}
              </td>
              <td class="text-xs-center">
                {{ props.item.document_source }}
              </td>
              <td class="text-xs-center">
                {{ props.item.typless_id || '' }}
              </td>
              <td v-if="['EX', 'FI'].includes(props.item.status)" class="text-xs-center">
                {{ props.item.exported_at | formatDateFilter }}
              </td>
              <td v-else class="text-xs-center">Neizvožen</td>
              <td
                v-if="['EX', 'CF', 'EE', 'FI', 'DP'].includes(props.item.status)"
                class="text-xs-center"
              >
                {{ props.item.confirmed_at | formatDateFilter }}
              </td>
              <td v-else class="text-xs-center">Nepotrjen</td>
              <td class="text-xs-center">
                {{ props.item.confirmed_by || 'Nepotrjen' }}
              </td>
              <td class="text-xs-center">
                {{ props.item.external_approver || '' }}
              </td>
              <td class="text-xs-center">
                {{
                  props.item.external_approval_date
                    ? $options.filters.formatDateFilter(props.item.external_approval_date)
                    : ''
                }}
              </td>
              <td class="text-xs-center">
                {{ props.item.external_rejection ? 'Zavrnjen' : 'Ne' }}
              </td>
              <td class="text-xs-center">
                {{ props.item.rejection }}
              </td>
              <td class="text-xs-center">
                <span v-if="props.item.finished_at">
                  {{ props.item.finished_at | formatDateFilter }}
                </span>
                <span v-else> Ne </span>
              </td>
              <td class="text-xs-center">
                {{ documentTypes[props.item.document_type] }}
              </td>
              <td class="text-xs-center d-flex">
                <VTooltip top>
                  <VBtn
                    slot="activator"
                    :disabled="
                      props.item.status === 'WR' ||
                      props.item.status === 'IP' ||
                      props.item.confirmed_by === 'Original eSlog'
                    "
                    flat
                    icon
                    color="info"
                    @click="returnInvoiceToProcessing(props.item.url)"
                  >
                    <VIcon>redo</VIcon>
                  </VBtn>
                  <span>Vrni v procesiranje</span>
                </VTooltip>
                <VTooltip top>
                  <VBtn
                    slot="activator"
                    :disabled="
                      !['EX', 'FI'].includes(props.item.status) || props.item.external_rejection
                    "
                    flat
                    icon
                    color="red"
                    @click="markInvoiceAsRejectedByExternalApi(props.item.id)"
                  >
                    <VIcon>cancel</VIcon>
                  </VBtn>
                  <span>Označi kot zavrnjen</span>
                </VTooltip>
              </td>
            </template>
            <template slot="no-data"> Za to poizvedbo ni nobenih zadetkov </template>
          </VDataTable>
        </v-card-text>
      </VCard>
    </VFlex>
  </VLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { generateQueryParams } from '@/helper';
import { exportInvoicesCsv, searchPartnersForFilter } from '@/services/api';
import debounce from 'lodash/debounce';

export default {
  name: 'InvoiceHistory',
  filters: {
    formatDateFilter(date) {
      return new Date(date).toLocaleString('de-DE').replace(',', '');
    },
  },
  data() {
    return {
      limit: 50,
      confirmedInvoices: false,
      searchModel: '',
      clientFilter: '',
      fromMenu: false,
      fromDate: null,
      toMenu: false,
      toDate: null,
      toDateFormatted: null,
      fromDateFormatted: null,
      documentType: { text: 'Vsi', value: 'ALL' },
      documentSource: { text: 'Vsi', value: 'ALL' },
      documentStatus: { text: 'Vsi', value: 'ALL' },
      selectedPartner: null,
      partnerSearchQuery: '',
      partnerSearchResults: [],
      loadingPartners: false,
      selectedExportPath: null,
      exportingCsv: false,
      exportPathItems: [
        { text: 'Pantheon', value: 'Pantheon' },
        { text: 'OpPIS', value: 'OpPIS' },
        { text: 'e-Line', value: 'e-Line' },
        { text: 'MojArhiv', value: 'MojArhiv' },
        { text: 'eracuni@unija.com', value: 'eracuni@unija.com' },
        { text: 'inbox@epostman.eu', value: 'inbox@epostman.eu' },
      ],
      headers: [
        { text: 'ID', value: 'url', align: 'right' },
        { text: 'Datum e-maila', value: 'sent_date', align: 'center' },
        { text: 'Pošiljatelj', value: 'from_mail', align: 'center' },
        {
          text: 'Zadeva e-maila / Davčna številka pošiljatelja',
          value: 'subject',
          align: 'center',
        },
        { text: 'Priponka', value: 'file_name', align: 'center' },
        { text: 'Partner (ime)', value: 'partner_name', align: 'center' },
        { text: 'Partner (davčna)', value: 'partner_vat_id', align: 'center' },
        { text: 'Država', value: 'partner_country', align: 'center' },
        { text: 'Izvozna pot', value: 'export_path', align: 'center' },
        { text: 'Zunanji ID', value: 'external_id', align: 'center' },
        { text: 'Vir dokumenta', value: 'document_source', align: 'center' },
        { text: 'Typless ID', value: 'typless_id', align: 'center' },
        { text: 'Datum izvoza', value: 'exported_at', align: 'center' },
        { text: 'Datum potrditve', value: 'confirmed_at', align: 'center' },
        { text: 'Potrdil', value: 'confirmed_by', align: 'center' },
        { text: 'Zunanji potrjevalec', value: 'external_approver', align: 'center' },
        { text: 'Čas zunanje potrditve', value: 'external_approval_date', align: 'center' },
        { text: 'Zavrnitev zunanjega servisa', value: 'external_rejection', align: 'center' },
        { text: 'Razlog zavrnitve', value: 'rejection', align: 'center' },
        { text: 'Knjižen v OpPis', value: 'finished_at', align: 'right' },
        { text: 'Tip dokumenta', value: 'document_type', align: 'center' },
        { text: 'Akcije', value: 'actions', align: 'center' },
      ],
      documentTypeItems: [
        { text: 'Vsi', value: 'ALL' },
        { text: 'Prejeti račun', value: 'INV' },
        { text: 'Ostalo', value: 'OST' },
        { text: 'Izdani račun', value: 'IRN' },
      ],
      documentStatusItems: [
        { text: 'Vsi', value: 'ALL' },
        { text: 'Potrjeni', value: 'CF' },
        { text: 'Duplicirani', value: 'DP' },
      ],
      documentSourceItems: [
        { text: 'Vsi', value: 'ALL' },
        { text: 'Mail', value: 'MAIL' },
        { text: 'Unija Scan', value: 'UNSC' },
      ],
      documentTypes: {
        INV: 'Prejeti račun',
        OST: 'Ostalo',
        IRN: 'Izdani račun',
      },
      invoiceCount: 0,
      pagination: {},
      previousPage: 1,
      paginationParams: {
        previous: null,
        current: null,
        next: null,
      },
      loading: false,
    };
  },
  computed: {
    ...mapGetters({
      invoices: 'processedInvoices',
    }),
    computedToDate() {
      return this.toDate ? this.$moment(this.toDate).format('DD.MM.YYYY') : '';
    },
    computedFromDate() {
      return this.fromDate ? this.$moment(this.fromDate).format('DD.MM.YYYY') : '';
    },
    filteredInvoices() {
      if (!this.clientFilter || this.clientFilter.trim() === '') {
        return this.invoices;
      }

      const filterText = this.clientFilter.toLowerCase();

      return this.invoices.filter((invoice) => {
        const id = invoice.url ? invoice.url.split('/').slice(-2, -1)[0] : '';
        const partnerName = invoice.partner_name || '';
        const partnerVat = invoice.partner_vat_id || '';
        const typlessId = invoice.typless_id || '';
        const fromMail = invoice.from_mail || '';
        const fileName = invoice.file_name || '';
        const subject = invoice.subject || '';
        const exportPath = invoice.export_path || '';

        const exportedAtRaw = invoice.exported_at || '';
        const exportedAtFormatted = invoice.exported_at
          ? new Date(invoice.exported_at).toLocaleString('de-DE').replace(',', '')
          : '';

        return (
          id.toLowerCase().includes(filterText) ||
          partnerName.toLowerCase().includes(filterText) ||
          partnerVat.toLowerCase().includes(filterText) ||
          typlessId.toLowerCase().includes(filterText) ||
          fromMail.toLowerCase().includes(filterText) ||
          fileName.toLowerCase().includes(filterText) ||
          subject.toLowerCase().includes(filterText) ||
          exportPath.toLowerCase().includes(filterText) ||
          exportedAtRaw.toLowerCase().includes(filterText) ||
          exportedAtFormatted.toLowerCase().includes(filterText)
        );
      });
    },
  },
  created() {
    this.debouncedPartnerSearch = debounce(this.fetchPartners, 400);
  },
  mounted() {
    this.toDate = this.$moment().format('YYYY-MM-DD');
    this.fromDate = this.$moment().subtract(1, 'weeks').format('YYYY-MM-DD');
    this.queryInvoicesWithParams();
  },
  beforeDestroy() {
    if (this.debouncedPartnerSearch) {
      this.debouncedPartnerSearch.cancel();
    }
  },
  watch: {
    partnerSearchQuery(val) {
      if (val && val.length >= 2) {
        this.debouncedPartnerSearch(val);
      }
    },
    selectedPartner() {
      this.queryInvoicesWithParams();
    },
    pagination: {
      async handler(val, oldVal) {
        if (!oldVal.page || (val.page === oldVal.page && val.rowsPerPage === oldVal.rowsPerPage)) {
          return;
        }
        if (val.rowsPerPage !== oldVal.rowsPerPage) {
          await this.queryInvoicesWithParams();
          return;
        }

        if (val.page > oldVal.page) {
          const current = this.paginationParams.next;
          const { previous, next } = await this.getProcessedInvoices(current);
          this.setPaginationParams(previous, current, next);
          return;
        }

        const current = this.paginationParams.previous;
        const { previous, next } = await this.getProcessedInvoices(current);
        this.setPaginationParams(previous, current, next);
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions([
      'getProcessedInvoices',
      'setSnackBarStateAction',
      'returnToProcessing',
      'setExternalRejectionAction',
    ]),
    async fetchPartners(query) {
      this.loadingPartners = true;
      try {
        const { data } = await searchPartnersForFilter(query);
        const results = data.map((p) => ({
          id: p.id,
          displayText: `${p.name} (${p.vat_id || ''})`,
          ...p,
        }));
        if (this.selectedPartner && !results.find((r) => r.id === this.selectedPartner.id)) {
          results.unshift(this.selectedPartner);
        }
        this.partnerSearchResults = results;
      } catch (err) {
        console.log(err);
      } finally {
        this.loadingPartners = false;
      }
    },
    buildFilterParams() {
      const params = {
        created_at_after: this.fromDate,
        created_at_before: this.toDate,
        status:
          this.documentStatus.value === 'ALL' || this.documentStatus === 'ALL'
            ? ''
            : this.documentStatus.value || this.documentStatus,
        limit: this.pagination.rowsPerPage,
        q: this.searchModel,
        document_type:
          this.documentType.value === 'ALL' || this.documentType === 'ALL'
            ? ''
            : this.documentType.value || this.documentType,
        document_source: this.documentSource.value || this.documentSource,
        partner: this.selectedPartner ? this.selectedPartner.id : '',
        export_path: this.selectedExportPath || '',
      };
      return params;
    },
    async exportCsv() {
      this.exportingCsv = true;
      try {
        const params = this.buildFilterParams();
        delete params.limit;
        const queryParams = generateQueryParams(params);
        const response = await exportInvoicesCsv(queryParams);
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'history-export.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.log(err);
        this.setSnackBarStateAction({
          snackBar: true,
          color: 'error',
          text: 'Napaka pri izvozu CSV. Poskusite znova.',
        });
      } finally {
        this.exportingCsv = false;
      }
    },
    parsePaginationParams(searchParamsOrUrlString) {
      let searchParams = null;
      try {
        searchParams = new URL(searchParamsOrUrlString).searchParams.toString();
      } catch {
        searchParams = searchParamsOrUrlString;
      }
      return searchParams;
    },
    setPaginationParams(previous, current, next) {
      this.paginationParams.next = this.parsePaginationParams(next);
      this.paginationParams.current = this.parsePaginationParams(current);
      this.paginationParams.previous = this.parsePaginationParams(previous);
    },
    async refreshInvoicesAtCurrentPage() {
      await this.getProcessedInvoices(this.paginationParams.current);
    },
    async queryInvoicesWithParams() {
      try {
        const params = this.buildFilterParams();

        this.loading = true;

        const queryParams = generateQueryParams(params);
        const { count, next } = await this.getProcessedInvoices(queryParams);
        this.invoiceCount = count;
        this.paginationParams.current = this.parsePaginationParams(queryParams);
        this.paginationParams.next = this.parsePaginationParams(next);
      } catch (err) {
        console.log(err);
      } finally {
        this.pagination.page = 1;
        this.loading = false;
      }
    },
    async returnInvoiceToProcessing(invoiceKey) {
      try {
        await this.returnToProcessing(invoiceKey);
        this.setSnackBarStateAction({
          snackBar: true,
          color: 'info',
          text: 'Račun je bil vrnjen v procesiranje',
        });
        await this.refreshInvoicesAtCurrentPage();
      } catch (e) {
        if (e.response && e.response.status === 403) {
          this.setSnackBarStateAction({
            snackBar: true,
            color: 'red',
            text: 'Nimate pravic za vračanje računa v procesiranje.',
          });
          return;
        }

        this.setSnackBarStateAction({
          snackBar: true,
          color: 'error',
          text: 'Napaka pri vračanju računa v ponovno procesiranje. Kontaktirajte administratorja.',
        });
      }
    },
    async markInvoiceAsRejectedByExternalApi(invoiceId) {
      try {
        await this.setExternalRejectionAction(invoiceId);
        this.setSnackBarStateAction({
          snackBar: true,
          color: 'info',
          text: 'Račun je bil označen kot zavrnjen s strani zunanjega sistema.',
        });
        await this.refreshInvoicesAtCurrentPage();
      } catch (e) {
        console.log(e);
        if (e.response && e.response.status === 403) {
          this.setSnackBarStateAction({
            snackBar: true,
            color: 'red',
            text: 'Nimate pravic za zavračanje računov.',
          });
          return;
        }

        this.setSnackBarStateAction({
          snackBar: true,
          color: 'error',
          text: 'Napaka pri zavračanju računa. Kontaktirajte administratorja.',
        });
      }
    },
  },
};
</script>

<style scoped></style>
